/**
 * =========================================================
 * Judge.me Cards Carousel Handler (既存デザイン寄せ)
 * ---------------------------------------------------------
 * 1) Cards Carousel のカード(.jdgm-card)クリックで商品ページへ遷移（同一タブ）
 * 2) スワイプ誤爆防止（dx/dy）
 * 3) レビュータイトル(太字)＋日付をカードへ注入
 * 4) REVIEW 見出しを all-reviews リンク化（再描画に追従）
 * 5) 見出し下：平均評価非表示、件数を「◯◯件のレビュー」形式に
 * 6) クリック遷移を「画像のみ」に限定
 * =========================================================
 */

(() => {
  "use strict";

  /** ========= 設定 ========= */
  const DEBUG = true;

  // テーマ変更に強い：ブロックID固定を避け、Cards Carousel ブロックを広く特定
  const ROOT_SELECTOR = [
    '[id^="shopify-block-"][id*="__judge_me_reviews_cards_carousel_"]',
    ".jdgm-widget.jdgm-cards-carousel",
    '[data-widget="cards-carousel"]',
  ].join(",");

  const CARD_SELECTOR = ".jdgm-card";
  const SWIPE_THRESHOLD = 10; // px

  // モーダル内の「商品名っぽい」クリック要素（<a>じゃないケース対応）
  const MODAL_PRODUCT_TRIGGER_SELECTORS = [
    ".jm-product-variant-card__product-link",                 // p role="button" など
    '[class*="product-variant-card"] [role="button"]',
    '[class*="product-link"][role="button"]',
    '.jm-modal a[href*="/products/"]',
    '.jdgm-modal a[href*="/products/"]',
    '[data-url*="/products/"]',
  ].join(",");

  const HTML_REDIRECT_CLASS = "jdgm-direct-redirect";
  const FALLBACK_TIMEOUT_MS = 5000;
  const FALLBACK_POLL_MS = 120;

  /** ========= 追加要件（今回） ========= */

  // ② 画像のみクリックで遷移させる
  const IMAGE_ONLY_CLICK = true;

  // “画像判定”のための候補（必要なら追加）
  const IMAGE_HIT_SELECTORS = [
    "img",
    "picture",
    "video",
    // Judge.me側の差分吸収用（存在すれば拾う）
    '[class*="media"]',
    '[class*="image"]',
    '[class*="thumbnail"]',
  ].join(",");

  const HEADER_REWRITE_ENABLED = true;

  // en サイト判定（Liquid が使えないため hostname で判定）
  const IS_EN_SITE = location.hostname.includes("en.picollet.com");

  /** REVIEW 見出しのセレクター（jdgm-title / jdgm-carousel-title 両対応） */
  const REVIEW_TITLE_SELECTORS = "h2.jdgm-title, h2.jdgm-carousel-title";

  /** ========= ログ ========= */
  const log = (...a) => DEBUG && console.log("[JDGM CC]", ...a);
  const warn = (...a) => DEBUG && console.warn("[JDGM CC]", ...a);

  /** ========= 状態 ========= */
  const state = {
    cache: new Map(), // reviewId -> { title, dateText, productUrl }
    lastDown: null,   // {x,y,card}
    redirecting: false,
    cleanupFns: [],
  };

  // 事故防止：残留クラスで「何も起きない」を防ぐ
  document.documentElement.classList.remove(HTML_REDIRECT_CLASS);

  /** ========= util ========= */
  function closestRoot(el) {
    return el?.closest?.(ROOT_SELECTOR) || null;
  }

  function getReviewIdFromCard(card) {
    return (
      card?.getAttribute?.("data-review-id") ||
      card?.dataset?.reviewId ||
      null
    );
  }

  function ensureAbsoluteUrl(url) {
    if (!url) return "";
    const s = String(url).trim();
    if (!s) return "";
    if (s.startsWith("http://") || s.startsWith("https://")) return s;
    if (s.startsWith("/")) return s;
    // handle だけ来た場合の救済
    if (!s.includes("/")) return `/products/${s}`;
    return `/${s}`;
  }

  function getAllReviewsUrl() {
    const root = window.routes?.rootUrl || "/";
    const normalizedRoot = root.endsWith("/") ? root : `${root}/`;
    return new URL(`${normalizedRoot}pages/all-reviews`, window.location.origin).toString();
  }

  function sanitizeDateRaw(raw) {
    if (!raw) return "";
    // "2026-01-28T23:32:24.393:" のように末尾が壊れてても落ちにくくする
    return String(raw).trim().replace(/[^0-9T:\-+.Z]/g, "");
  }

  function formatDate(raw) {
    if (!raw) return "";
    const s = String(raw).trim();
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;

    const cleaned = sanitizeDateRaw(s);
    const d = new Date(cleaned);
    if (Number.isNaN(d.getTime())) return s;

    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = String(d.getFullYear());
    return `${mm}/${dd}/${yyyy}`;
  }

  /** ========= review object から拾う（幅広対応） ========= */
  function pickId(review) {
    return (
      review?.id ||
      review?.review_id ||
      review?.uuid ||
      review?.external_id ||
      review?.hash ||
      null
    );
  }

  function pickTitle(review) {
    return (
      review?.title ||
      review?.review_title ||
      review?.headline ||
      review?.subject ||
      review?.review_heading ||
      ""
    );
  }

  function pickDateRaw(review) {
    return (
      review?.created_at ||
      review?.createdAt ||
      review?.created_at_iso ||
      review?.published_at ||
      review?.date ||
      ""
    );
  }

  function pickProductUrl(review) {
    const direct =
      review?.product_url ||
      review?.productUrl ||
      review?.product?.url ||
      review?.product?.product_url ||
      review?.product_handle ||
      review?.product?.handle ||
      review?.product_slug ||
      review?.product?.slug ||
      "";
    return ensureAbsoluteUrl(direct);
  }

  function upsertCacheFromReviews(reviews) {
    if (!Array.isArray(reviews)) return;

    // デバッグ：最初だけ中身を見る
    if (DEBUG && !window.__JDGM_CC_DUMPED__) {
      window.__JDGM_CC_DUMPED__ = true;
      log("Sample review object:", reviews[0]);
    }

    for (const r of reviews) {
      const id = pickId(r);
      if (!id) continue;

      const next = {
        title: pickTitle(r),
        dateText: formatDate(pickDateRaw(r)),
        productUrl: pickProductUrl(r),
      };

      const prev = state.cache.get(id) || {};
      state.cache.set(id, {
        title: prev.title || next.title,
        dateText: prev.dateText || next.dateText,
        productUrl: prev.productUrl || next.productUrl,
      });
    }
  }

  /** 本文pを1つに正規化（複数pによるGrid重なり防止） */
  function normalizeCardBodyParagraphs(card) {
    const textBox = card.querySelector(".jdgm-text");
    if (!textBox) return;

    // 直下の裸テキストノードを <p class="jdgm-review-body"> で包む。
    // .jdgm-text は display:contents のため、包まないと匿名グリッドアイテムとして
    // 画像列（row1/col1）へ自動配置され、本文が縦組みに潰れる。
    // 空白だけのノードは包まない（空の <p> がグリッドの1セルを占有して行が崩れるため）。
    Array.from(textBox.childNodes)
      .filter((n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim())
      .forEach((n) => {
        const p = document.createElement("p");
        p.className = "jdgm-review-body";
        p.textContent = n.textContent;
        textBox.replaceChild(p, n);
      });

    const bodyPs = Array.from(textBox.querySelectorAll("p")).filter(
      (p) => !p.classList.contains("jdgm-review-title")
    );
    if (bodyPs.length === 0) return;

    if (bodyPs.length === 1) {
      bodyPs[0].classList.add("jdgm-review-body");
      return;
    }

    const merged = bodyPs
      .map((p) => (p.innerText || "").trim())
      .filter(Boolean)
      .join("\n")
      .replace(/\s*\n\s*/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    const first = bodyPs[0];
    first.classList.add("jdgm-review-body");
    first.textContent = merged;
    bodyPs.slice(1).forEach((p) => p.remove());
  }

  /** ========= カードへ注入 ========= */
  function injectIntoCard(card) {
    const reviewId = getReviewIdFromCard(card);
    if (!reviewId) return;

    const meta = state.cache.get(reviewId);
    if (!meta) return;

    if (meta.productUrl && !card.dataset.jdgmProductUrl) {
      card.dataset.jdgmProductUrl = meta.productUrl;
    }

    const textBox = card.querySelector(".jdgm-text");
    if (!textBox) return;

    if (meta.title && !textBox.querySelector(".jdgm-review-title")) {
      const titleEl = document.createElement("p");
      titleEl.className = "jdgm-review-title";
      titleEl.textContent = meta.title;

      const bodyP = textBox.querySelector("p:not(.jdgm-review-title)");
      if (bodyP) {
        textBox.insertBefore(titleEl, bodyP);
        bodyP.classList.add("jdgm-review-body");
      } else {
        textBox.prepend(titleEl);
      }
    }

    if (meta.dateText && !card.querySelector(".jdgm-review-date")) {
      const dateEl = document.createElement("div");
      dateEl.className = "jdgm-review-date";
      dateEl.textContent = meta.dateText;

      const rating = card.querySelector(".jdgm-rating-section");
      if (rating && rating.parentNode) {
        rating.parentNode.insertBefore(dateEl, rating.nextSibling);
      } else {
        card.appendChild(dateEl);
      }
    }

    normalizeCardBodyParagraphs(card);
  }

  function injectAll() {
    document
      .querySelectorAll(`${ROOT_SELECTOR} ${CARD_SELECTOR}[data-review-id]`)
      .forEach(injectIntoCard);
  }

  /** テーマが出力した総レビュー件数（#total_reviews）。無ければ空文字 */
  function getTotalReviewsFromTheme() {
    const el = document.querySelector("#total_reviews");
    // display:none の要素なので innerText ではなく textContent を使う
    const n = String(el ? el.textContent : "").replace(/[^\d]/g, "");
    return n || "";
  }

  /** レビュー件数をテキストから抽出（括弧 or "◯◯件" 形式） */
  function extractReviewCount(text) {
    const t = String(text || "").replace(/\s+/g, " ").trim();
    const m =
      t.match(/[\(\（]\s*([\d,]+)\s*[\)\）]/) ||
      t.match(/\b([\d,]+)\s*(?:reviews?|件)/i);
    return m ? m[1] : "";
  }

  /**
   * ヘッダーの評価ブロック（.jdgm-header-info 〜 .jdgm-rating-text）を書き換え
   * DOM構造: .jdgm-average-rating > .jdgm-stars + span.jdgm-rating-text
   * - 4.81 を非表示、件数を「◯◯件のレビュー」に統一（textContent で安全に）
   * - .jdgm-stars は触らず維持
   */
  function rewriteHeaderAll() {
    if (!HEADER_REWRITE_ENABLED) return;

    const roots = document.querySelectorAll(ROOT_SELECTOR);
    roots.forEach((root) => {
      const headerInfo = root.querySelector(".jdgm-header-info");
      const avgRating = headerInfo
        ? headerInfo.querySelector(".jdgm-average-rating")
        : root.querySelector(".jdgm-average-rating");
      if (!avgRating || avgRating.closest(".jdgm-card")) return;

      const ratingText = avgRating.querySelector("span.jdgm-rating-text");
      if (!ratingText) return;
      if (ratingText.getAttribute("data-jdgm-cc-header-rewritten") === "1")
        return;

      // 件数の正はテーマのメタフィールド（#total_reviews）。
      // DOM 文字列からの抽出は Judge.me の表記形式に依存するためフォールバック扱い。
      const countNum =
        getTotalReviewsFromTheme() ||
        String(extractReviewCount(ratingText.textContent || "") || "")
          .replace(/,/g, "")
          .trim();

      // 件数が取れないうちは「処理済み」にしない（後続の MutationObserver で再試行させる）
      if (!countNum) return;

      const formatted = IS_EN_SITE
        ? `${countNum} Reviews`
        : `${countNum}件のレビュー`;

      ratingText.setAttribute("data-jdgm-cc-header-rewritten", "1");
      const a = document.createElement("a");
      a.href = getAllReviewsUrl();
      a.textContent = formatted;
      a.style.color = "inherit";
      a.style.textDecoration = "none";
      ratingText.textContent = "";
      ratingText.appendChild(a);
    });
  }

  /** REVIEW 見出しをリンク化（Cards Carousel 内のみ・再描画時も再適用） */
  function wrapReviewTitleWithLink() {
    const roots = document.querySelectorAll(ROOT_SELECTOR);
    roots.forEach((root) => {
      const titles = root.querySelectorAll(REVIEW_TITLE_SELECTORS);
      titles.forEach((h2) => {
        if (h2.querySelector("a[href*='all-reviews']")) return;

        const text = (h2.textContent || "").trim();
        // en では Hextom が "Customer Reviews" 等に翻訳するため完全一致では判定しない。
        // 描画途中の空要素だけを除外する。
        if (!text) return;

        const a = document.createElement("a");
        a.href = getAllReviewsUrl();
        a.style.color = "inherit";
        a.style.textDecoration = "none";
        // 既存の子ノード（Hextomが翻訳対象とするテキストノード等）をそのままアンカーへ移す。
        // textContent で作り直すと、Hextom の初回パスより先に実行された場合に
        // 原文のまま固定されるおそれがあるため。
        while (h2.firstChild) a.appendChild(h2.firstChild);

        h2.appendChild(a);
        h2.setAttribute("data-jdgm-cc-title-linked", "1");
      });
    });
  }

  /** ========= 重要：jdgmBuildMediaCards を “定義された瞬間” にフック ========= */
  function findReviewsArrayInArgs(args) {
    // args のどこかに「レビュー配列」が入ることが多いので探索
    for (const a of args) {
      if (!a) continue;

      if (Array.isArray(a) && a.length && typeof a[0] === "object") {
        return a;
      }
      if (typeof a === "object") {
        if (Array.isArray(a.reviews) && a.reviews.length) return a.reviews;
        if (Array.isArray(a.data?.reviews) && a.data.reviews.length) return a.data.reviews;
      }
    }
    return null;
  }

  function hookWindowFunction(propName, wrapFactory) {
    // 既に function があるなら即wrap
    if (typeof window[propName] === "function" && !window[propName].__jdgm_cc_wrapped) {
      window[propName] = wrapFactory(window[propName]);
      window[propName].__jdgm_cc_wrapped = true;
      log(`Wrapped existing ${propName}`);
      return;
    }

    // 後から代入されるのを拾う
    let _val;
    try {
      Object.defineProperty(window, propName, {
        configurable: true,
        enumerable: false,
        get() { return _val; },
        set(v) {
          if (typeof v === "function" && !v.__jdgm_cc_wrapped) {
            _val = wrapFactory(v);
            _val.__jdgm_cc_wrapped = true;
            log(`Hooked ${propName} via setter`);
          } else {
            _val = v;
          }
        },
      });
      log(`Installed setter hook for ${propName}`);
    } catch (e) {
      warn(`Failed to defineProperty for ${propName}`, e);
    }
  }

  // まとめて再処理（MO/Build後に叩く用）
  let scheduled = false;
  function scheduleApply() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      wrapReviewTitleWithLink();
      injectAll();
      rewriteHeaderAll();
    });
  }

  hookWindowFunction("jdgmBuildMediaCards", (orig) => {
    return function (...args) {
      try {
        const reviews = findReviewsArrayInArgs(args);
        if (reviews) upsertCacheFromReviews(reviews);
      } catch (e) {
        warn("Error parsing jdgmBuildMediaCards args:", e);
      }

      const res = orig.apply(this, args);

      // DOM反映後に注入 & 見出しも追従
      setTimeout(scheduleApply, 0);
      return res;
    };
  });

  /** ========= window.open を同一タブ遷移へ差し替え（保険ルート用） ========= */
  function hookWindowOpenToSameTab() {
    const original = window.open;
    let active = true;

    window.open = function (url, target, features) {
      if (active && url) {
        const u = ensureAbsoluteUrl(url);
        log("Intercept window.open -> same tab:", u);
        window.location.assign(u);
        return null;
      }
      return original.apply(this, arguments);
    };

    return () => {
      active = false;
      window.open = original;
      log("window.open unhooked");
    };
  }

  /** ========= productUrl が取れない時：モーダル経由で拾う ========= */
  function startModalFallbackRedirect() {
    if (state.redirecting) return;
    state.redirecting = true;

    document.documentElement.classList.add(HTML_REDIRECT_CLASS);

    const unhookOpen = hookWindowOpenToSameTab();

    const cleanup = () => {
      state.redirecting = false;
      document.documentElement.classList.remove(HTML_REDIRECT_CLASS);

      // 片付け
      while (state.cleanupFns.length) {
        try { state.cleanupFns.pop()(); } catch (_) {}
      }
      try { unhookOpen(); } catch (_) {}

      log("Fallback cleanup done");
    };

    const tryTrigger = () => {
      const modalRoot = document.querySelector(".jm-modal, .jdgm-modal");
      const scope = modalRoot || document;
      const el = scope.querySelector(MODAL_PRODUCT_TRIGGER_SELECTORS);
      if (!el) return false;

      // data-url/href を直接使えるならそれが最強
      const dataUrl = el.getAttribute("data-url") || el.dataset?.url;
      if (dataUrl && String(dataUrl).includes("/products/")) {
        const u = ensureAbsoluteUrl(dataUrl);
        log("Found product via data-url:", u);
        window.location.assign(u);
        return true;
      }

      if (el.tagName === "A" && el.href && el.href.includes("/products/")) {
        log("Found product via <a>:", el.href);
        window.location.assign(el.href);
        return true;
      }

      // クリックで window.open が起きる想定（hookが拾う）
      try {
        log("Auto-click modal product trigger:", el);
        el.click();
        return true;
      } catch (e) {
        warn("Failed to click modal trigger:", e);
        return false;
      }
    };

    // MutationObserver：DOMに要素が出たら拾う（opacity 0 でもOK）
    const mo = new MutationObserver(() => {
      if (tryTrigger()) cleanup();
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
    state.cleanupFns.push(() => mo.disconnect());

    // ポーリング保険：observer取り逃し対策
    const iv = setInterval(() => {
      if (tryTrigger()) cleanup();
    }, FALLBACK_POLL_MS);
    state.cleanupFns.push(() => clearInterval(iv));

    // タイムアウト
    const to = setTimeout(() => {
      warn("Fallback timeout (no product trigger found)");
      cleanup();
    }, FALLBACK_TIMEOUT_MS);
    state.cleanupFns.push(() => clearTimeout(to));
  }

  /** ========= ② 画像クリック判定 ========= */
  function isImageAreaClick(e, card) {
    if (!IMAGE_ONLY_CLICK) return true;
    const t = e.target;

    if (!t || !(t instanceof Element)) return false;

    // テキスト/星/日付側は除外（= 遷移しない）
    if (t.closest(".jdgm-text, .jdgm-rating-section, .jdgm-review-date")) return false;

    // 画像/メディアっぽい要素にヒットしているか
    const hit = t.closest(IMAGE_HIT_SELECTORS);
    if (hit && card.contains(hit)) return true;

    // 背景画像のdivを使うケース（inline style のみ軽量対応）
    const div = t.closest("div");
    if (div && card.contains(div)) {
      const style = div.getAttribute("style") || "";
      if (/background-image\s*:\s*url\(/i.test(style)) return true;
    }

    return false;
  }

  /** ========= クリック(カード→遷移) ========= */
  function onPointerDownCapture(e) {
    const card = e.target.closest(CARD_SELECTOR);
    if (!card) return;
    if (!closestRoot(card)) return;

    state.lastDown = { x: e.clientX, y: e.clientY, card };
  }

  function onClickCapture(e) {
    // 矢印は除外（従来通り動く）
    if (e.target.closest(".jdgm-arrow")) return;

    const card = e.target.closest(CARD_SELECTOR);
    if (!card) return;
    if (!closestRoot(card)) return;

    // 画像以外のクリック：モーダルを出さないためイベントを完全抑止
    if (!isImageAreaClick(e, card)) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === "function")
        e.stopImmediatePropagation();
      return;
    }

    // スワイプ誤爆防止
    const d = state.lastDown;
    if (d && d.card === card) {
      const dx = Math.abs((e.clientX || 0) - (d.x || 0));
      const dy = Math.abs((e.clientY || 0) - (d.y || 0));
      if (dx > SWIPE_THRESHOLD || dy > SWIPE_THRESHOLD) {
        log("Swipe detected -> skip redirect", { dx, dy });
        return;
      }
    }

    // まず productUrl が取れていれば直遷移
    const reviewId = getReviewIdFromCard(card);
    const cached = reviewId ? state.cache.get(reviewId) : null;

    const url =
      card.dataset.jdgmProductUrl ||
      cached?.productUrl ||
      "";

    if (url) {
      const u = ensureAbsoluteUrl(url);
      log("Direct redirect:", u);

      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();

      window.location.assign(u);
      return;
    }

    // productUrl が無いならモーダル経由（ただし画像クリック時のみ）
    log("No productUrl -> fallback via modal");
    startModalFallbackRedirect();
    // ※ここではイベントは殺さない（Judge.meにモーダルを開かせるため）
  }

  /** ========= 初期化 ========= */
  function initOnce() {
    if (window.__JDGM_CC_INSTALLED__) return;
    window.__JDGM_CC_INSTALLED__ = true;

    document.addEventListener("pointerdown", onPointerDownCapture, true);
    document.addEventListener("click", onClickCapture, true);

    // DOM変化で注入を継続（cloneやスライドでカードが増えるため）
    const mo = new MutationObserver(() => scheduleApply());
    mo.observe(document.documentElement, { childList: true, subtree: true });

    // 初期注入 + 見出し
    scheduleApply();

    log("Installed (stable + req ①②)");
    // デバッグ確認用に公開
    window.__JDGM_CC_STATE__ = state;
  }

  // ROOTが出るまで待ってから init（不要なページで動かさないため）
  const start = Date.now();
  const t = setInterval(() => {
    if (document.querySelector(ROOT_SELECTOR)) {
      clearInterval(t);
      initOnce();
    } else if (Date.now() - start > 15000) {
      clearInterval(t);
      warn("Init timeout: carousel root not found");
    }
  }, 300);
})();